import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, LogIn, LogOut, User as UserIcon, LifeBuoy } from 'lucide-react';
import { useAuthStore } from '../../stores/auth';
import { api } from '../../utils/api';
import { canOpenProfileRoute } from '../../lib/profileAccess';
import { nextPunchAction, punchEndpoint, punchLabel, type PunchAction } from './punchMenu';
import { Dropdown, DropdownDivider, DropdownItem } from './TopBarPrimitives';

export function TopBarAccount({open,onToggle,onClose,displayName,initials,accent}:{
  open:boolean;onToggle:()=>void;onClose:()=>void;displayName:string;initials:string;accent:string;
}) {
  const navigate=useNavigate();
  const {user,logout}=useAuthStore();
  // ── Punch In / Punch Out (Captain U, 2026-07-16) ─────────────────────────
  // Attendance is now STRICTLY punch-driven — merely opening the app no longer
  // writes the pay table. The web equivalent of the mobile PunchCard lives in
  // the avatar dropdown. `punchedIn` is the SERVER answer (GET /hr/attendance/
  // today reads only source='punch' sessions), so the menu shows exactly one
  // of Punch In / Punch Out.
  const [punchedIn, setPunchedIn] = useState<boolean | null>(null);
  const [punchBusy, setPunchBusy] = useState(false);
  const [punchMsg, setPunchMsg] = useState<string | null>(null);

  // Punch status — refetch when the avatar menu opens so the label is fresh.
  useEffect(() => {
    if (!open) return;
    let active = true;
    api.get('/hr/attendance/today')
      .then((res) => { if (active) setPunchedIn(Boolean(res?.data?.data?.punchedIn)); })
      .catch(() => { if (active) setPunchedIn(null); });
    return () => { active = false; };
  }, [open]);

  // Punch In / Punch Out — GPS-stamped, exactly like the mobile PunchCard.
  // The server requires {lat,lng} (punch is location-proofed), so we read the
  // browser geolocation; a denied/unavailable fix surfaces an inline message
  // instead of silently sending bogus coordinates.
  const doPunch = async (action: PunchAction) => {
    if (punchBusy) return;
    setPunchBusy(true);
    setPunchMsg(null);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        if (!('geolocation' in navigator)) {
          reject(new Error('no-geo'));
          return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        });
      });
      await api.post(punchEndpoint(action), {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      });
      setPunchedIn(action === 'punch-in');
      setPunchMsg(action === 'punch-in' ? 'Punched in' : 'Punched out');
    } catch (err: unknown) {
      const geoDenied =
        typeof err === 'object' && err !== null && 'code' in err &&
        (err as GeolocationPositionError).code === 1;
      const noGeo = err instanceof Error && err.message === 'no-geo';
      setPunchMsg(
        geoDenied || noGeo
          ? 'Location needed to punch — allow location access'
          : 'Could not punch — try again',
      );
    } finally {
      setPunchBusy(false);
    }
  };

  return (
      <div className="relative">
        <button
          type="button"
          data-tour="account-menu"
          onClick={onToggle}
          className="flex items-center justify-center rounded-full font-semibold max-sm:min-h-[40px] max-sm:min-w-[40px]"
          style={{
            width: 30, height: 30,
            background: accent,
            color: '#fff', fontSize: 11,
          }}
          title={displayName}
        >
          {initials || 'U'}
        </button>
        {open && (
          <Dropdown width={220} align="right">
            <div className="px-2.5 py-2" style={{ fontSize: 12 }}>
              <div style={{ fontWeight: 500, color: 'var(--ink-0)' }}>{displayName}</div>
              <div style={{ color: 'var(--ink-3)' }}>{user?.email}</div>
            </div>
            <DropdownDivider />
            {/* Punch In / Punch Out — attendance is strictly punch-driven.
                Shows exactly one action based on the server's punch state. */}
            {punchedIn === true ? (
              <DropdownItem onClick={() => { doPunch(nextPunchAction(true)); }}>
                <LogOut size={14} />
                <span className="flex-1">{punchLabel(true, punchBusy)}</span>
              </DropdownItem>
            ) : (
              <DropdownItem onClick={() => { doPunch(nextPunchAction(false)); }}>
                <LogIn size={14} />
                <span className="flex-1">{punchLabel(false, punchBusy)}</span>
              </DropdownItem>
            )}
            {punchMsg && (
              <div
                className="px-2.5 py-1 flex items-center gap-1.5"
                style={{ fontSize: 11, color: 'var(--ink-3)' }}
              >
                <Clock size={11} />
                <span>{punchMsg}</span>
              </div>
            )}
            <DropdownDivider />
            <DropdownItem onClick={() => { navigate('/profile'); onClose(); }}>
              <UserIcon size={14} />
              <span className="flex-1">Profile</span>
            </DropdownItem>
            {canOpenProfileRoute(user, '/settings') && (
              <DropdownItem onClick={() => { navigate('/settings'); onClose(); }}>
                <span className="flex-1" style={{ marginLeft: 22 }}>Settings</span>
              </DropdownItem>
            )}
            {/* L229-WEB-1 — every role can report bugs / request features */}
            <DropdownItem onClick={() => { navigate('/support/my'); onClose(); }}>
              <LifeBuoy size={14} />
              <span className="flex-1">Support</span>
            </DropdownItem>
            <DropdownDivider />
            <DropdownItem
              onClick={async () => { await logout(); onClose(); navigate('/login'); }}
              danger
            >
              <LogOut size={14} />
              <span className="flex-1">Logout</span>
            </DropdownItem>
          </Dropdown>
        )}
      </div>
  );
}
